import React from 'react';
import { Button } from '../../ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../ui/card';
import { Badge } from '../../ui/badge';
import { Share2, Globe, MessageSquare, Mail, Users, Copy, Clock, Award, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

const SIGNUP_LINK = 'https://legacyforge.family/pro';

const TEXT_TEMPLATE = `Hey [Name]! I'm part of a new family legacy platform called Legacy Forge and we're looking for trusted professionals to join our advisor network. As an early partner, you'd get a permanent "Founding Advisor" badge and featured listing. It's free to join — takes about 5 minutes:

${SIGNUP_LINK}

Let me know if you have any questions!`;

const EMAIL_TEMPLATE_BODY = `Hi [Name],

I wanted to reach out because I'm building something I think you'd be a great fit for. Legacy Forge is a family-focused platform that connects families with trusted professional advisors — financial planners, estate attorneys, CPAs, therapists, real estate experts, and insurance specialists.

We're in our early launch phase and I'm personally inviting professionals I trust to join our Founding Advisor network. Here's what that means for you:

- Permanent "Founding Advisor" badge on your profile
- Featured listing in our Professional Directory
- Direct access to families actively seeking professional guidance
- Free to join — no fees or commitments

It takes about 5 minutes to create your profile:
${SIGNUP_LINK}

I'd love to have you on board. Let me know if you have any questions.

Best,
[Your Name]`;

const SOCIAL_TEMPLATE = `Are you a financial advisor, estate attorney, CPA, real estate expert, insurance specialist, or family therapist?

I'm looking for trusted professionals to join Legacy Forge — a platform that connects families with advisors who help them build and protect their legacy.

Early partners earn a permanent "Founding Advisor" badge and featured directory listing. Free to join.

Sign up here: ${SIGNUP_LINK}`;

const copy = (text, label) => {
  navigator.clipboard.writeText(text);
  toast.success(`${label} copied!`);
};

export default function RecruitTab({ signupStats }) {
  return (
    <div className="space-y-6">
      <Card className="bg-slate-800 border-slate-700">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Share2 className="h-5 w-5 text-amber-400" />
            Recruit Professional Advisors
          </CardTitle>
          <CardDescription className="text-slate-400">
            Share these ready-to-send messages to invite professionals to join Legacy Forge.
            New signups are automatically approved and featured as Founding Advisors during the launch period.
          </CardDescription>
        </CardHeader>
      </Card>

      {signupStats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="bg-slate-800 border-slate-700">
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-amber-400" data-testid="stat-total-approved">{signupStats.total_approved}</p>
              <p className="text-slate-400 text-sm mt-1">Approved Advisors</p>
            </CardContent>
          </Card>
          <Card className="bg-slate-800 border-slate-700">
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-green-400" data-testid="stat-founding">{signupStats.founding_advisors}</p>
              <p className="text-slate-400 text-sm mt-1">Founding Advisors</p>
            </CardContent>
          </Card>
          <Card className="bg-slate-800 border-slate-700">
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-sky-400" data-testid="stat-referred">{signupStats.referred_signups}</p>
              <p className="text-slate-400 text-sm mt-1">Referred Signups</p>
            </CardContent>
          </Card>
          <Card className="bg-slate-800 border-slate-700">
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-purple-400" data-testid="stat-pending">{signupStats.total_pending}</p>
              <p className="text-slate-400 text-sm mt-1">Pending Review</p>
            </CardContent>
          </Card>
        </div>
      )}

      {signupStats && Object.keys(signupStats.signups_by_type).length > 0 && (
        <Card className="bg-slate-800 border-slate-700">
          <CardContent className="p-6">
            <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
              <Users className="h-4 w-4 text-amber-400" />
              Advisors by Specialty
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {Object.entries(signupStats.signups_by_type).map(([type, count]) => (
                <div key={type} className="flex items-center justify-between bg-slate-900 rounded-lg px-4 py-2 border border-slate-700">
                  <span className="text-slate-300 text-sm">{type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</span>
                  <Badge className="bg-amber-600/20 text-amber-400 border-amber-600/30">{count}</Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {signupStats && signupStats.recent_signups.length > 0 && (
        <Card className="bg-slate-800 border-slate-700">
          <CardContent className="p-6">
            <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
              <Clock className="h-4 w-4 text-amber-400" />
              Recent Signups (Last 30 Days)
            </h3>
            <div className="space-y-3">
              {signupStats.recent_signups.map((signup, i) => (
                <div key={i} className="flex items-center justify-between bg-slate-900 rounded-lg px-4 py-3 border border-slate-700">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-amber-600/20 flex items-center justify-center">
                      <Award className="h-4 w-4 text-amber-400" />
                    </div>
                    <div>
                      <p className="text-white text-sm font-medium">{signup.name}</p>
                      <p className="text-slate-500 text-xs">
                        {signup.professional_type?.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                        {signup.referred_by && <span className="text-green-400 ml-2">(Referred)</span>}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className={signup.status === 'approved' ? 'bg-green-600/20 text-green-400 border-green-600/30' : 'bg-amber-600/20 text-amber-400 border-amber-600/30'}>
                      {signup.status}
                    </Badge>
                    <span className="text-slate-500 text-xs">
                      {new Date(signup.created_at).toLocaleDateString()}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <Card className="bg-slate-800 border-slate-700">
        <CardContent className="p-6">
          <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
            <Globe className="h-4 w-4 text-amber-400" />
            Direct Signup Link
          </h3>
          <div className="flex items-center gap-2 bg-slate-900 rounded-lg p-3 border border-slate-600">
            <code className="text-amber-400 text-sm flex-1 break-all" data-testid="signup-link">{SIGNUP_LINK}</code>
            <Button size="sm" className="bg-amber-600 hover:bg-amber-700 text-white shrink-0" data-testid="copy-link-btn" onClick={() => copy(SIGNUP_LINK, 'Link')}>
              <Copy className="h-4 w-4 mr-1" />
              Copy
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card className="bg-slate-800 border-slate-700">
        <CardContent className="p-6">
          <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
            <MessageSquare className="h-4 w-4 text-green-400" />
            Text Message Template
          </h3>
          <div className="bg-slate-900 rounded-lg p-4 border border-slate-600 mb-3">
            <p className="text-slate-300 text-sm whitespace-pre-wrap" data-testid="text-template">{TEXT_TEMPLATE}</p>
          </div>
          <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white" data-testid="copy-text-btn" onClick={() => copy(TEXT_TEMPLATE, 'Text message')}>
            <Copy className="h-4 w-4 mr-1" />
            Copy Text
          </Button>
        </CardContent>
      </Card>

      <Card className="bg-slate-800 border-slate-700">
        <CardContent className="p-6">
          <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
            <Mail className="h-4 w-4 text-blue-400" />
            Email Template
          </h3>
          <div className="bg-slate-900 rounded-lg p-4 border border-slate-600 mb-3">
            <p className="text-amber-400 text-sm font-medium mb-2">Subject: Join Legacy Forge as a Founding Advisor</p>
            <p className="text-slate-300 text-sm whitespace-pre-wrap" data-testid="email-template">{EMAIL_TEMPLATE_BODY}</p>
          </div>
          <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="copy-email-btn" onClick={() => copy(`Subject: Join Legacy Forge as a Founding Advisor\n\n${EMAIL_TEMPLATE_BODY}`, 'Email template')}>
            <Copy className="h-4 w-4 mr-1" />
            Copy Email
          </Button>
        </CardContent>
      </Card>

      <Card className="bg-slate-800 border-slate-700">
        <CardContent className="p-6">
          <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
            <Users className="h-4 w-4 text-sky-400" />
            LinkedIn / Social Post Template
          </h3>
          <div className="bg-slate-900 rounded-lg p-4 border border-slate-600 mb-3">
            <p className="text-slate-300 text-sm whitespace-pre-wrap" data-testid="social-template">{SOCIAL_TEMPLATE}</p>
          </div>
          <Button size="sm" className="bg-sky-600 hover:bg-sky-700 text-white" data-testid="copy-social-btn" onClick={() => copy(SOCIAL_TEMPLATE, 'Social post')}>
            <Copy className="h-4 w-4 mr-1" />
            Copy Post
          </Button>
        </CardContent>
      </Card>

      <Card className="bg-slate-800/50 border-slate-700 border-dashed">
        <CardContent className="p-6">
          <h3 className="text-amber-400 font-semibold mb-3">Recruitment Tips</h3>
          <ul className="text-slate-400 text-sm space-y-2">
            <li className="flex items-start gap-2">
              <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />
              <span><strong className="text-slate-300">Personal outreach wins.</strong> A direct text or call converts far better than a mass post.</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />
              <span><strong className="text-slate-300">Lead with the value.</strong> "Founding Advisor" status and featured listing are compelling for professionals building their practice.</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />
              <span><strong className="text-slate-300">Start with people you know.</strong> Your personal network of trusted professionals is the strongest starting point.</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />
              <span><strong className="text-slate-300">Free to join, verified before listing.</strong> New signups appear in the directory once you approve them in the Oversight tab.</span>
            </li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
}
